import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import reportJobService from '@api/v1/reportJobService'
import reportService from '@api/v1/reportService'
import reportPackService, { saveBlob } from '@api/v1/reportPackService'
import useReadState from '@hooks/useReadState'
import { isResultAvailable } from '@config/queueConfig'
import { reportNameMap } from '@components/reports/queue/jobIdentity'
import { fmtWhenInZone } from '@components/reports/queue/queueFormat'
import { COMBINE_MAX, combinedFileName, isCombinable, localTimeZone } from './packFormat'

/**
 * "Roll up" finished runs into ONE workbook, on demand (spec 172).
 *
 * Lists the caller's own finished standard-report runs. cb-api fetches each one's file from
 * Centerbase and merges them; if any chosen run's file can't be prepared, nothing is combined
 * and the answer names that run — a download silently missing a report you picked would read
 * as complete.
 */
const CombineRunsPanel = () => {
  const tz = localTimeZone()
  const runsQuery = useQuery({
    queryKey: ['report-pack-combinable'],
    queryFn: () => reportJobService.list({ scope: 'mine', state: 'ready', limit: 50 }),
    staleTime: 30_000,
  })
  const catalogQuery = useQuery({ queryKey: ['reports-list'], queryFn: () => reportService.list(''), staleTime: 5 * 60 * 1000 })
  const state = useReadState('report-pack-combinable', runsQuery)
  const names = useMemo(() => reportNameMap(catalogQuery.data), [catalogQuery.data])
  // No `processId` test here: the jobs LIST never carries one (`LIST_BASE_COLUMNS` in
  // cb-api's jobs store), so requiring it emptied this panel for every user — live on QA
  // it showed "need at least two" over 20 finished runs. `/combine` re-reads each job and
  // refuses, by name, one that has no processId. What the list CAN say is expiry, and when
  // the run finished: Centerbase's own file for it is certain only for a day (spec 205).
  const runs = useMemo(() => {
    const now = Date.now()
    return (state === 'ready' ? runsQuery.data?.jobs || [] : []).filter((j) => isResultAvailable(j, now) && isCombinable(j, now))
  }, [state, runsQuery.data])

  const [picked, setPicked] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(null)

  const toggle = (id) => setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= COMBINE_MAX ? cur : [...cur, id]))
  const nameOf = (j) => names.get(String(j.reportId)) || `Report ${j.reportId}`

  const combine = async () => {
    setError(null); setDone(null); setBusy(true)
    try {
      const { blob } = await reportPackService.combine(picked, tz)
      saveBlob(blob, combinedFileName())
      setDone(`Downloaded one workbook with ${picked.length} reports.`)
    } catch (e) {
      setError(e?.message || "The runs couldn't be combined into one workbook.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <section aria-labelledby="combine-runs-title" className="mt-8">
      <h2 id="combine-runs-title" className="text-[15px] font-semibold text-gray-900">Combine finished runs into one workbook</h2>
      <p className="mt-1 text-[11.5px] text-gray-600">
        Pick 2 to {COMBINE_MAX} of your standard-report runs from the last 23 hours. Each sheet is Centerbase&apos;s own Excel export of that run, with its filters.
        Centerbase can remove a run&apos;s file once it is a day old, so older runs aren&apos;t listed: run the report again to include it.
      </p>
      <div className="mt-3">
        {state === 'loading' || state === 'idle' ? (
          <p className="text-[12px] text-gray-600">Loading your finished runs…</p>
        ) : state !== 'ready' ? (
          <p role="alert" className="text-[12px] text-red-800">
            Your finished runs couldn&apos;t be loaded. This is not an empty list.{' '}
            <button type="button" onClick={() => runsQuery.refetch()} className="font-semibold text-cb-accent-text underline">Try again</button>
          </p>
        ) : runs.length < 2 ? (
          <p className="text-[12px] text-gray-700" data-testid="combine-too-few">
            You need at least two standard-report runs that finished in the last 23 hours. Run reports from their pages, or queue them, and they&apos;ll appear here.
          </p>
        ) : (
          <>
            <ul className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2">
              {runs.map((j) => {
                const checked = picked.includes(j.id)
                return (
                  <li key={j.id}>
                    <label className="flex items-center gap-2 text-[12.5px] text-gray-800">
                      <input type="checkbox" checked={checked} disabled={!checked && picked.length >= COMBINE_MAX} onChange={() => toggle(j.id)} />
                      <span className="font-medium">{nameOf(j)}</span>
                      <span className="text-gray-600">{fmtWhenInZone(j.createdAt, tz)}</span>
                    </label>
                  </li>
                )
              })}
            </ul>
            <div className="mt-2 flex items-center gap-3">
              <button type="button" className="btn-cb-primary" disabled={busy || picked.length < 2} onClick={combine}>
                {busy ? 'Combining…' : `Download one workbook (${picked.length})`}
              </button>
              {done && <span role="status" className="text-[12px] text-gray-700">{done}</span>}
            </div>
          </>
        )}
        {error && <p role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-800">{error}</p>}
      </div>
    </section>
  )
}

export default CombineRunsPanel
